/**
 * Calendar dates, in the timezone the person is actually sitting in.
 *
 * ## The bug this exists to end
 *
 * `new Date().toISOString().split("T")[0]` reads as "today, as YYYY-MM-DD", and
 * it is wrong for most of the world. `toISOString()` converts to UTC first, so
 * what comes back is the UTC calendar date, not the local one. In Dhaka
 * (UTC+6) every moment between 00:00 and 05:59 local is still *yesterday* in
 * UTC, so a dashboard opened at half past midnight asked the API for the
 * previous day and got yesterday's work — while the row highlighting beside it,
 * which used local getters, disagreed and highlighted today.
 *
 * The same call applied to a date somebody picked is worse, because it is
 * silent all day: a picker yields a Date at LOCAL midnight, which is
 * `2026-09-02T18:00:00Z` in Dhaka, and `.toISOString()` turns that back into
 * `"2026-09-02"` — the day before the one that was clicked. West of UTC the
 * error runs the other way and appears in the evening instead.
 *
 * The fix is to never round-trip through UTC. `getFullYear`/`getMonth`/
 * `getDate` are already local, so reading them directly is both correct and
 * cheaper than formatting.
 *
 * `moment` is deliberately not used here. It is in the bundle already, but this
 * runs in stores that import nothing else from it, and three lines of `padStart`
 * do not need a date library.
 */

/** A `Date` (or anything `Date` can parse) as `YYYY-MM-DD` in local time. */
export const toLocalDateString = (
  value: Date | string | number = new Date(),
): string => {
  const date = value instanceof Date ? value : new Date(value);

  // An unparseable input would otherwise stringify as "NaN-NaN-NaN" and be sent
  // to the API as if it were a real date.
  if (Number.isNaN(date.getTime())) return "";

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
};

/**
 * Today, locally.
 *
 * A function rather than a module-level const on purpose. Several of these
 * screens live in a tab that stays open for days, and a const captured at
 * import time keeps saying yesterday once the clock passes midnight — which is
 * the same class of defect as the UTC one, arriving a few hours later.
 */
export const todayLocal = (): string => toLocalDateString();

/**
 * `days` from a local `YYYY-MM-DD`, still local.
 *
 * Parsing the string with `new Date("2026-09-02")` would read it as UTC
 * midnight and shift the result for anyone east of UTC, so the parts are fed to
 * the `Date` constructor separately, which builds a LOCAL date. Month overflow
 * is handled by `Date` itself: day 32 of September is 2 October.
 */
export const addDaysLocal = (date: string, days: number): string => {
  const [year, month, day] = date.split("-").map(Number);

  if (!year || !month || !day) return "";

  return toLocalDateString(new Date(year, month - 1, day + days));
};
